/**
 * Error Mapper Utility
 * 
 * Maps backend error messages and codes to i18n translation keys.
 */

import i18n from '../i18n';

// Map backend IBAN error codes to translation keys
const IBAN_ERROR_CODE_MAP: Record<string, string> = {
  'IBAN_EMPTY': 'errors.iban.empty',
  'IBAN_TOO_SHORT': 'errors.iban.tooShort',
  'IBAN_TOO_LONG': 'errors.iban.tooLong',
  'IBAN_INVALID_CHARACTERS': 'errors.iban.invalidCharacters',
  'IBAN_INVALID_COUNTRY_CODE': 'errors.iban.invalidCountryCode',
  'IBAN_UNSUPPORTED_COUNTRY': 'errors.iban.unsupportedCountry',
  'IBAN_INVALID_LENGTH_FOR_COUNTRY': 'errors.iban.invalidLengthForCountry',
  'IBAN_INVALID_CHECK_DIGITS': 'errors.iban.invalidCheckDigits',
  'IBAN_INVALID_CHECKSUM': 'errors.iban.invalidChecksum',
  'IBAN_INVALID_BBAN_FORMAT': 'errors.iban.invalidBbanFormat',
};

// Map common backend error messages to translation keys
const ERROR_MESSAGE_MAP: Record<string, string> = {
  'Invalid IBAN format': 'errors.iban.invalid',
  'Invalid IBAN checksum': 'errors.iban.invalidChecksum',
  'IBAN is required': 'errors.iban.empty',
  'Account name is required': 'errors.account.nameRequired',
  'Bank is required': 'errors.account.bankRequired',
  'IBAN must contain only letters and digits': 'errors.iban.invalidCharacters',
  'Country code must be two letters': 'errors.iban.invalidCountryCode',
  'Check digits must be two numeric digits': 'errors.iban.invalidCheckDigits',
  'A shared access with this email already exists': 'errors.sharedAccess.emailAlreadyExists',
  'This email address belongs to an active Budgira account with bank accounts. A user cannot be both an account owner and a shared access guest.': 'errors.sharedAccess.userHasOwnAccounts',
  'Email already in use': 'errors.auth.emailAlreadyInUse',
  'Email address is already in use': 'errors.auth.emailAlreadyInUse',
};

// Patterns for error messages that need parameter extraction
const ERROR_PATTERNS: Array<{
  pattern: RegExp;
  translationKey: string;
  extractParams: (match: RegExpMatchArray) => Record<string, string>;
}> = [
  {
    pattern: /IBAN must be at least (\d+) characters/i,
    translationKey: 'errors.iban.tooShort',
    extractParams: (match) => ({ min: match[1] || '15' }),
  },
  {
    pattern: /IBAN must not exceed (\d+) characters/i,
    translationKey: 'errors.iban.tooLong',
    extractParams: (match) => ({ max: match[1] || '34' }),
  },
  {
    pattern: /Unsupported country code: ([A-Z]{2})/i,
    translationKey: 'errors.iban.unsupportedCountry',
    extractParams: (match) => ({ code: match[1] || '' }),
  },
  {
    pattern: /Invalid IBAN length for (.+)\. Expected (\d+) characters, got (\d+)/i,
    translationKey: 'errors.iban.invalidLengthForCountry',
    extractParams: (match) => ({
      country: match[1] || '',
      expected: match[2] || '',
      actual: match[3] || '',
    }),
  },
  {
    pattern: /Invalid BBAN format for (.+)/i,
    translationKey: 'errors.iban.invalidBbanFormat',
    extractParams: (match) => ({ country: match[1] || '' }),
  },
  {
    pattern: /An account with this name already exists/i,
    translationKey: 'errors.account.duplicateName',
    extractParams: () => ({}),
  },
  {
    pattern: /An account with this IBAN already exists/i,
    translationKey: 'errors.account.duplicateIban',
    extractParams: () => ({}),
  },
];

/**
 * Maps a backend error message to a localized message.
 * 
 * @param error - The error message from backend
 * @param errorCode - Optional error code from backend response
 * @returns Localized error message
 */
export function mapBackendError(error: string, errorCode?: string): string {
  // First, try to map by error code
  if (errorCode && IBAN_ERROR_CODE_MAP[errorCode]) {
    const translationKey = IBAN_ERROR_CODE_MAP[errorCode];
    const translated = i18n.t(translationKey);
    if (translated !== translationKey) {
      return translated;
    }
  }

  // Then, try exact message match
  if (ERROR_MESSAGE_MAP[error]) {
    const translationKey = ERROR_MESSAGE_MAP[error];
    const translated = i18n.t(translationKey);
    if (translated !== translationKey) {
      return translated;
    }
  }

  // Finally, try pattern matching
  for (const { pattern, translationKey, extractParams } of ERROR_PATTERNS) {
    const match = error.match(pattern);
    if (match) {
      const params = extractParams(match);
      const translated = i18n.t(translationKey, params);
      if (translated !== translationKey) {
        return translated;
      }
    }
  }

  // If no mapping found, return original error
  return error;
}

/**
 * Extracts error message from various error response formats.
 * 
 * @param error - Axios error or generic error
 * @returns Object with message and optional error code
 */
export function extractErrorInfo(error: unknown): { message: string; errorCode?: string } {
  if (error && typeof error === 'object') {
    // Axios error with response
    if ('response' in error && error.response && typeof error.response === 'object') {
      const response = error.response as { 
        data?: { 
          error?: string | { code?: string; message?: string }; 
          errorCode?: string; 
          message?: string 
        } 
      };
      if (response.data) {
        // Handle nested error object format: { error: { code, message } }
        if (response.data.error && typeof response.data.error === 'object') {
          const errorObj = response.data.error;
          return { 
            message: errorObj.message || '', 
            errorCode: errorObj.code 
          };
        }
        // Handle flat format: { error: string }
        if (typeof response.data.error === 'string') {
          return { message: response.data.error, errorCode: response.data.errorCode };
        }
        // Handle { message: string }
        if (response.data.message) {
          return { message: response.data.message, errorCode: response.data.errorCode };
        }
      }
    }
    
    // Standard Error object
    if (error instanceof Error) {
      return { message: error.message };
    }
    
    // Plain object with message
    if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
      return { message: (error as { message: string }).message };
    }
  }

  return { message: String(error) };
}

/**
 * Maps and localizes an error from various formats.
 * 
 * @param error - The error to map (Axios error, Error object, string, etc.)
 * @returns Localized error message
 */
export function localizeError(error: unknown): string {
  const { message, errorCode } = extractErrorInfo(error);
  return mapBackendError(message, errorCode);
}
